import {
  addCycle,
  daysUntil,
  deriveNextPaymentDate,
  parseBillingCycle,
} from "./billingSchedule";
import {
  normalizeAmountToMonthly,
  convertCurrency,
  DEMO_CURRENCY_RATES,
} from "./currency";
import { normalizeToISO } from "./dateUtils";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";

interface TotalsOptions {
  includePayg?: boolean;
  excludeCancelled?: boolean;
  activeOnly?: boolean;
  displayCurrency: string;
  rates?: Record<string, number>;
  today?: Date;
}

export type PeriodChargeStatus = "Paid" | "Due" | "Upcoming";

export interface PeriodCharge {
  date: Date;
  source: "actual" | "expected";
}

type PeriodPayment = {
  subscription_id: string;
  payment_date: string;
  status: string;
};

function toUtcDate(value: string | Date | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function listPeriodCharges(
  sub: DatabaseSubscription,
  periodStart: Date,
  periodEnd: Date,
  paymentHistory: PeriodPayment[] = [],
  today: Date = new Date(),
): PeriodCharge[] {
  const startDate = toUtcDate(sub.start_date) ?? toUtcDate(sub.created_at);
  const paidForSub = paymentHistory.filter(
    (payment) =>
      payment.subscription_id === sub.id && payment.status === "paid",
  );

  const actuals = paidForSub
    .map((payment) => toUtcDate(payment.payment_date))
    .filter(
      (date): date is Date =>
        !!date && date >= periodStart && date <= periodEnd,
    )
    .sort((a, b) => a.getTime() - b.getTime());

  const charges: PeriodCharge[] = actuals.map((date) => ({
    date,
    source: "actual",
  }));

  const isPayg = (sub.billing_type || "recurring") === "pay_as_you_go";
  if (isPayg) {
    return charges;
  }

  const { frequency, customFrequencyDays } = parseBillingCycle(
    sub.billing_cycle,
  );
  if (!frequency) {
    return charges;
  }

  const derived = deriveNextPaymentDate({
    billingCycle: sub.billing_cycle,
    storedNextPaymentDate: sub.next_payment_date,
    paidDates: paidForSub.map((payment) => payment.payment_date),
    today,
  });
  if (!derived) {
    return charges;
  }

  const actualKeys = new Set(actuals.map((date) => normalizeToISO(date)));
  let cursor = derived;
  let guard = 0;
  while (cursor < periodStart && guard < 400) {
    cursor = addCycle(cursor, frequency, customFrequencyDays);
    guard += 1;
  }
  while (cursor <= periodEnd && guard < 400) {
    const key = normalizeToISO(cursor);
    const afterStart = !startDate || cursor >= startDate;
    if (!actualKeys.has(key) && cursor >= periodStart && afterStart) {
      charges.push({ date: new Date(cursor), source: "expected" });
    }
    cursor = addCycle(cursor, frequency, customFrequencyDays);
    guard += 1;
  }

  return charges.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function statusForCharges(
  charges: PeriodCharge[],
  today: Date,
): PeriodChargeStatus {
  const expected = charges.filter((charge) => charge.source === "expected");
  if (expected.length === 0) {
    return "Paid";
  }
  if (expected.some((charge) => daysUntil(charge.date, today) <= 0)) {
    return "Due";
  }
  return "Upcoming";
}

/**
 * Calculate monthly "running rate" (current spend rate, not period-specific)
 * Used for dashboard monthly totals
 */
export const calculateTotals = (
  subscriptions: DatabaseSubscription[],
  options: TotalsOptions,
) => {
  const {
    includePayg = true,
    excludeCancelled = true,
    activeOnly = false,
    displayCurrency,
    rates,
  } = options;

  const monthlyTotal = subscriptions.reduce((sum, sub) => {
    if (excludeCancelled && sub.status === "cancelled") return sum;
    if (activeOnly && sub.status !== "active") return sum;
    // Defensive: skip if cancellation_date is in the past (status may not have flipped yet)
    if (sub.cancellation_date && new Date(sub.cancellation_date) < new Date())
      return sum;
    if (!includePayg && (sub.billing_type || "recurring") === "pay_as_you_go")
      return sum;

    const frequency =
      sub.billing_cycle === "monthly"
        ? ("monthly" as const)
        : sub.billing_cycle === "yearly"
          ? ("yearly" as const)
          : sub.billing_cycle === "weekly"
            ? ("weekly" as const)
            : ("monthly" as const);

    const monthlyAmount = normalizeAmountToMonthly(
      sub.amount,
      frequency,
      undefined,
    );
    const converted = convertCurrency(
      monthlyAmount,
      sub.currency,
      displayCurrency,
      rates ?? DEMO_CURRENCY_RATES,
    );

    return sum + converted;
  }, 0);

  return { monthlyTotal, yearlyTotal: monthlyTotal * 12 };
};

function effectivePeriodForSub(
  sub: DatabaseSubscription,
  periodStart: Date,
  periodEnd: Date,
  options: Pick<TotalsOptions, "includePayg" | "excludeCancelled">,
): { periodStart: Date; periodEnd: Date } | null {
  const { includePayg = true, excludeCancelled = true } = options;
  if (excludeCancelled && sub.status === "cancelled") {
    return null;
  }
  if (!includePayg && (sub.billing_type || "recurring") === "pay_as_you_go") {
    return null;
  }

  const cancellationDate = toUtcDate(sub.cancellation_date);
  const effectivePeriodEnd = cancellationDate
    ? new Date(Math.min(periodEnd.getTime(), cancellationDate.getTime()))
    : periodEnd;
  if (effectivePeriodEnd < periodStart) {
    return null;
  }

  const startDate = toUtcDate(sub.start_date) ?? toUtcDate(sub.created_at);
  if (startDate && startDate > effectivePeriodEnd) {
    return null;
  }

  return { periodStart, periodEnd: effectivePeriodEnd };
}

/**
 * Calculate actual spend for a specific time period (date-aware).
 * One charge per billing cycle: recorded payments plus unpaid derived dates.
 */
export const calculatePeriodSpend = (
  subscriptions: DatabaseSubscription[],
  periodStart: Date,
  periodEnd: Date,
  options: Omit<TotalsOptions, "activeOnly">,
  paymentHistory?: PeriodPayment[],
): { periodTotal: number } => {
  const { displayCurrency, rates, today = new Date() } = options;

  const periodTotal = subscriptions.reduce((sum, sub) => {
    const bounds = effectivePeriodForSub(sub, periodStart, periodEnd, options);
    if (!bounds) {
      return sum;
    }

    const charges = listPeriodCharges(
      sub,
      bounds.periodStart,
      bounds.periodEnd,
      paymentHistory,
      today,
    );
    if (charges.length === 0) {
      return sum;
    }

    const converted = convertCurrency(
      sub.amount * charges.length,
      sub.currency,
      displayCurrency,
      rates ?? DEMO_CURRENCY_RATES,
    );

    return sum + converted;
  }, 0);

  return { periodTotal };
};

export interface SubscriptionBreakdownItem {
  id: string;
  name: string;
  amount: number;
  currency: string;
  billingCycle: string;
  billingType: string;
  paymentCount: number;
  totalAmount: number;
  convertedTotal: number;
  displayReason: PeriodChargeStatus;
  detailedReason: string;
  chargeDate: Date | null;
  endsInPeriod: boolean;
  endDate?: string;
}

/**
 * Get detailed breakdown of subscriptions for a specific period.
 * One charge per billing cycle. Status is Paid, Due, or Upcoming.
 */
export const getSubscriptionPeriodBreakdown = (
  subscriptions: DatabaseSubscription[],
  periodStart: Date,
  periodEnd: Date,
  options: Omit<TotalsOptions, "activeOnly">,
  paymentHistory?: PeriodPayment[],
): SubscriptionBreakdownItem[] => {
  const { displayCurrency, rates, today = new Date() } = options;
  const breakdown: SubscriptionBreakdownItem[] = [];

  subscriptions.forEach((sub) => {
    const bounds = effectivePeriodForSub(sub, periodStart, periodEnd, options);
    if (!bounds) {
      return;
    }

    const cancellationDate = toUtcDate(sub.cancellation_date);
    const endsInPeriod = !!(
      cancellationDate &&
      cancellationDate >= periodStart &&
      cancellationDate <= periodEnd
    );

    const charges = listPeriodCharges(
      sub,
      bounds.periodStart,
      bounds.periodEnd,
      paymentHistory,
      today,
    );
    if (charges.length === 0) {
      return;
    }

    const paymentCount = charges.length;
    const totalAmount = sub.amount * paymentCount;
    const convertedTotal = convertCurrency(
      totalAmount,
      sub.currency,
      displayCurrency,
      rates ?? DEMO_CURRENCY_RATES,
    );
    const displayReason = statusForCharges(charges, today);

    breakdown.push({
      id: sub.id,
      name: sub.name,
      amount: sub.amount,
      currency: sub.currency,
      billingCycle: formatBillingCycle(sub.billing_cycle || "monthly"),
      billingType: sub.billing_type || "recurring",
      paymentCount,
      totalAmount,
      convertedTotal,
      displayReason,
      detailedReason: displayReason,
      chargeDate: paymentCount === 1 ? charges[0].date : null,
      endsInPeriod,
      endDate: endsInPeriod ? sub.cancellation_date! : undefined,
    });
  });

  // Sort by converted total descending
  return breakdown.sort((a, b) => b.convertedTotal - a.convertedTotal);
};

function formatBillingCycle(cycle: string): string {
  const map: Record<string, string> = {
    weekly: "Weekly",
    fortnightly: "Fortnightly",
    monthly: "Monthly",
    every_2_months: "Every 2 Months",
    quarterly: "Quarterly",
    yearly: "Yearly",
  };
  return map[cycle] || "Monthly";
}
